import { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const card = { background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };
const th   = { padding: '14px', background: '#f1870e', color: 'white', textAlign: 'left', fontSize: '14px' };
const td   = { padding: '14px', borderBottom: '1px solid #eee', fontSize: '14px' };
const summaryCard = {
  background: 'linear-gradient(135deg,#f1870e,#ff9800)', color: 'white',
  padding: '24px', borderRadius: '14px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
};
const tabBtn = (active) => ({
  padding: '10px 22px', borderRadius: '8px', border: 'none',
  cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  background: active ? '#f1870e' : '#fff3e0',
  color: active ? 'white' : '#f1870e', transition: 'all 0.2s'
});
const badge = (color) => ({
  display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
  fontSize: '12px', fontWeight: '600',
  background: color === 'green' ? '#e8f5e9' : color === 'blue' ? '#e3f2fd' : '#fff8e1',
  color: color === 'green' ? '#2e7d32' : color === 'blue' ? '#1565c0' : '#f57f17'
});

const ROLE_CONFIG = {
  invigilator:  { label: 'Invigilator',  color: '#1565c0', bg: '#e3f2fd' },
  tca:          { label: 'TCA',          color: '#6a1b9a', bg: '#f3e5f5' },
  system_admin: { label: 'System Admin', color: '#e65100', bg: '#fff3e0' },
  attender:     { label: 'Attender',     color: '#2e7d32', bg: '#e8f5e9' },
  super_admin:  { label: 'Super Admin',  color: '#bf360c', bg: '#fbe9e7' },
};

// ✅ Resolves role name from multiple possible field names
const resolveRole = (item) =>
  item.role_name || item.roleName || item.role || item.user_role || null;

// ✅ Resolves rate from multiple possible field names
const resolveRate = (item) =>
  Number(item.rate_per_shift ?? item.ratePerShift ?? item.rate ?? item.shift_rate ?? 0);

const roleBadge = (roleName) => {
  if (!roleName) return <span style={{ color: '#999' }}>—</span>;
  const cfg = ROLE_CONFIG[roleName] || { label: roleName, color: '#555', bg: '#f5f5f5' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
      fontSize: '12px', fontWeight: 600, background: cfg.bg, color: cfg.color
    }}>{cfg.label}</span>
  );
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const EmptyRow = ({ cols, loading }) => (
  <tr><td colSpan={cols} style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
    {loading ? 'Loading...' : 'No data found'}
  </td></tr>
);

const ADMIN_ROLES = ['super_admin', 'system_admin', 'tca'];

const RemunerationSummary = () => {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [view, setView]           = useState('summary');
  const [summary, setSummary]     = useState([]);
  const [weekly, setWeekly]       = useState([]);
  const [monthly, setMonthly]     = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading]     = useState(false);

  const [searchName, setSearchName]       = useState('');
  const [searchResult, setSearchResult]   = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError]     = useState('');

  const totalAmount       = summary.reduce((a, i) => a + Number(i.total_amount  || 0), 0);
  const totalShifts       = summary.reduce((a, i) => a + Number(i.shifts_worked || 0), 0);
  const totalInvigilators = summary.length;

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sumRes, weekRes, monthRes, schedRes] = await Promise.all([
        axios.get('/remuneration/summary'),
        axios.get('/remuneration/weekly'),
        axios.get('/remuneration/monthly'),
        axios.get('/remuneration/schedule-wise')
      ]);
      setSummary(sumRes.data     || []);
      setWeekly(weekRes.data     || []);
      setMonthly(monthRes.data   || []);
      setSchedules(schedRes.data || []);
    } catch { toast.error('Failed to fetch remuneration data'); }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!searchName.trim()) { setSearchError('Please enter a name to search'); return; }
    setSearchError(''); setSearchLoading(true); setSearchResult(null);
    try {
      const res = await axios.get(`/remuneration/search?name=${encodeURIComponent(searchName.trim())}`);
      const raw = res.data;
      if (Array.isArray(raw)) {
        if (!raw.length) { setSearchResult({ found: false }); return; }
        const summaryMap = {};
        raw.forEach(s => {
          const key = s.invigilator_name || s.name;
          if (!summaryMap[key]) summaryMap[key] = { name: key, total_schedules: new Set(), total_shifts: 0, total_remuneration: 0 };
          if (s.exam_name) summaryMap[key].total_schedules.add(s.exam_name);
          if (s.is_present) summaryMap[key].total_shifts += 1;
          summaryMap[key].total_remuneration += Number(s.amount || 0);
        });
        setSearchResult({ found: true, summary: Object.values(summaryMap).map(x => ({ ...x, total_schedules: x.total_schedules.size })), shifts: raw });
      } else if (raw && typeof raw === 'object') {
        setSearchResult({
          found: raw.found !== false && (raw.summary?.length > 0 || raw.shifts?.length > 0),
          summary: raw.summary || [], shifts: raw.shifts || []
        });
      } else { setSearchResult({ found: false }); }
    } catch { toast.error('Search failed'); }
    finally { setSearchLoading(false); }
  };

  const clearSearch = () => { setSearchName(''); setSearchResult(null); setSearchError(''); };

  const renderSearchResult = () => {
    if (!searchResult) return null;
    if (!searchResult.found) return (
      <div style={{ ...card, marginBottom: '24px', color: '#e65100', textAlign: 'center' }}>
        No invigilator found matching "{searchName}"
      </div>
    );
    const shifts      = searchResult.shifts  || [];
    const summaryList = searchResult.summary || [];
    return (
      <div style={{ ...card, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: '#f1870e', margin: 0 }}>🔍 Search Results for "{searchName}"</h2>
          <button onClick={clearSearch} style={{ background: '#fff3e0', color: '#f1870e', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontWeight: '600' }}>✕ Clear</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '16px', marginBottom: '24px' }}>
          {summaryList.map((inv, i) => (
            <div key={i} style={{ background: 'linear-gradient(135deg,#f1870e,#ff9800)', color: 'white', padding: '20px', borderRadius: '12px' }}>
              <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '12px' }}>👤 {inv.name}</div>
              <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '4px' }}>📋 Schedules: <b>{inv.total_schedules}</b></div>
              <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '4px' }}>🕐 Shifts: <b>{inv.total_shifts}</b></div>
              <div style={{ fontSize: '18px', fontWeight: '700', marginTop: '8px' }}>₹ {Number(inv.total_remuneration).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <h3 style={{ color: '#f1870e', marginBottom: '14px' }}>Shift-wise Breakdown</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>S.No</th>
                {isAdmin && <th style={th}>Invigilator</th>}
                <th style={th}>Role</th>
                <th style={th}>Exam</th>
                <th style={th}>Shift</th>
                <th style={th}>Date</th>
                <th style={th}>Attendance</th>
                <th style={th}>Rate</th>
                <th style={th}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length > 0 ? shifts.map((s, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={td}>{i + 1}</td>
                  {isAdmin && <td style={{ ...td, fontWeight: '600' }}>{s.invigilator_name || s.name || '—'}</td>}
                  <td style={td}>{roleBadge(resolveRole(s))}</td>
                  <td style={td}>{s.exam_name || '—'}</td>
                  <td style={td}><span style={badge('blue')}>{s.shift_name || '—'}</span></td>
                  <td style={td}>{formatDate(s.shift_date)}</td>
                  <td style={td}><span style={badge(s.is_present ? 'green' : 'yellow')}>{s.is_present ? 'Present' : 'Absent'}</span></td>
                  <td style={td}>₹ {resolveRate(s).toFixed(2)}</td>
                  <td style={{ ...td, fontWeight: 'bold', color: '#f1870e' }}>₹ {Number(s.amount || 0).toFixed(2)}</td>
                </tr>
              )) : <tr><td colSpan={isAdmin ? 9 : 8} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No shift records found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSummary = () => (
    <div style={card}>
      <h2 style={{ color: '#f1870e', marginBottom: '20px' }}>Overall Remuneration by Role</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>S.No</th>
              <th style={th}>Name</th>
              <th style={th}>Role</th>
              <th style={th}>Exam(s)</th>
              <th style={th}>Shifts Worked</th>
              <th style={th}>Rate / Shift</th>
              <th style={th}>Total Amount</th>
            </tr>
          </thead>
          <tbody>
            {summary.length > 0 ? summary.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={td}>{i + 1}</td>
                <td style={{ ...td, fontWeight: '600' }}>{item.name}</td>
                {/* ✅ Fixed: resolveRole handles all field name variants */}
                <td style={td}>{roleBadge(resolveRole(item))}</td>
                <td style={td}>{item.exam_name || '—'}</td>
                <td style={td}><span style={badge('blue')}>{item.shifts_worked} shifts</span></td>
                {/* ✅ Fixed: resolveRate handles all field name variants */}
                <td style={td}>₹ {resolveRate(item).toFixed(2)}</td>
                <td style={{ ...td, color: '#f1870e', fontWeight: 'bold' }}>₹ {Number(item.total_amount).toFixed(2)}</td>
              </tr>
            )) : <EmptyRow cols={7} loading={loading} />}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderWeekly = () => (
    <div style={card}>
      <h2 style={{ color: '#f1870e', marginBottom: '20px' }}>Weekly Remuneration Breakdown</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>S.No</th><th style={th}>Week</th><th style={th}>Year</th>
              {isAdmin && <th style={th}>Name</th>}
              <th style={th}>Role</th>
              <th style={th}>Exam</th><th style={th}>Shifts</th>
              {isAdmin && <th style={th}>Total Staff</th>}
              <th style={th}>Rate</th><th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {weekly.length > 0 ? weekly.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={td}>{i + 1}</td>
                <td style={td}><span style={badge('blue')}>Week {item.week_number}</span></td>
                <td style={td}>{item.year}</td>
                {isAdmin && <td style={{ ...td, fontWeight: '600' }}>{item.name}</td>}
                {/* ✅ Fixed */}
                <td style={td}>{roleBadge(resolveRole(item))}</td>
                <td style={td}>{item.exam_name || '—'}</td>
                <td style={td}>{item.shifts_worked}</td>
                {isAdmin && <td style={td}><span style={badge('green')}>{item.total_invigilators}</span></td>}
                {/* ✅ Fixed */}
                <td style={td}>₹ {resolveRate(item).toFixed(2)}</td>
                <td style={{ ...td, color: '#f1870e', fontWeight: 'bold' }}>₹ {Number(item.total_amount).toFixed(2)}</td>
              </tr>
            )) : <EmptyRow cols={isAdmin ? 10 : 7} loading={loading} />}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderMonthly = () => (
    <div style={card}>
      <h2 style={{ color: '#f1870e', marginBottom: '20px' }}>Monthly Remuneration Breakdown</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>S.No</th><th style={th}>Month</th><th style={th}>Year</th>
              {isAdmin && <th style={th}>Name</th>}
              <th style={th}>Role</th>
              <th style={th}>Exam</th><th style={th}>Shifts</th>
              {isAdmin && <th style={th}>Total Staff</th>}
              <th style={th}>Rate</th><th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {monthly.length > 0 ? monthly.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={td}>{i + 1}</td>
                <td style={td}><span style={badge('blue')}>{item.month_name}</span></td>
                <td style={td}>{item.year}</td>
                {isAdmin && <td style={{ ...td, fontWeight: '600' }}>{item.name}</td>}
                {/* ✅ Fixed */}
                <td style={td}>{roleBadge(resolveRole(item))}</td>
                <td style={td}>{item.exam_name || '—'}</td>
                <td style={td}>{item.shifts_worked}</td>
                {isAdmin && <td style={td}><span style={badge('green')}>{item.total_invigilators}</span></td>}
                {/* ✅ Fixed */}
                <td style={td}>₹ {resolveRate(item).toFixed(2)}</td>
                <td style={{ ...td, color: '#f1870e', fontWeight: 'bold' }}>₹ {Number(item.total_amount).toFixed(2)}</td>
              </tr>
            )) : <EmptyRow cols={isAdmin ? 10 : 7} loading={loading} />}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSchedule = () => (
    <div style={card}>
      <h2 style={{ color: '#f1870e', marginBottom: '20px' }}>Schedule-wise Remuneration</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>S.No</th><th style={th}>Exam</th><th style={th}>Type</th>
              <th style={th}>Date</th><th style={th}>Shift</th>
              {isAdmin && <th style={th}>Name</th>}
              <th style={th}>Role</th>
              <th style={th}>Attendance</th><th style={th}>Rate</th><th style={th}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {schedules.length > 0 ? schedules.map((item, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={td}>{i + 1}</td>
                <td style={{ ...td, fontWeight: '600' }}>{item.exam_name}</td>
                <td style={td}>{item.exam_type || '—'}</td>
                <td style={td}>{formatDate(item.shift_date)}</td>
                <td style={td}><span style={badge('blue')}>{item.shift_name}</span></td>
                {isAdmin && <td style={td}>{item.invigilator_name}</td>}
                {/* ✅ Fixed */}
                <td style={td}>{roleBadge(resolveRole(item))}</td>
                <td style={td}><span style={badge(item.is_present ? 'green' : 'yellow')}>{item.is_present ? 'Present' : 'Absent'}</span></td>
                {/* ✅ Fixed */}
                <td style={td}>₹ {resolveRate(item).toFixed(2)}</td>
                <td style={{ ...td, color: '#f1870e', fontWeight: 'bold' }}>₹ {Number(item.amount || 0).toFixed(2)}</td>
              </tr>
            )) : <EmptyRow cols={isAdmin ? 10 : 9} loading={loading} />}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '32px', background: '#f4f6fb', minHeight: '100vh' }}>
      <h1 style={{ color: '#f1870e', marginBottom: '4px' }}>Remuneration Summary</h1>
      <p style={{ color: '#666', marginBottom: '24px' }}>View remuneration by role — weekly, monthly, and schedule-wise</p>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={summaryCard}><h3 style={{ margin: '0 0 8px' }}>Total Remuneration</h3><h1 style={{ margin: 0 }}>₹ {totalAmount.toFixed(2)}</h1></div>
        <div style={summaryCard}><h3 style={{ margin: '0 0 8px' }}>Total Shifts Worked</h3><h1 style={{ margin: 0 }}>{totalShifts}</h1></div>
        {isAdmin && <div style={summaryCard}><h3 style={{ margin: '0 0 8px' }}>Total Staff</h3><h1 style={{ margin: 0 }}>{totalInvigilators}</h1></div>}
      </div>

      {/* Search */}
      <div style={{ ...card, marginBottom: '24px' }}>
        <h3 style={{ color: '#f1870e', marginBottom: '14px' }}>🔍 {isAdmin ? 'Search Staff' : 'My Remuneration Details'}</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <input type="text"
              placeholder={isAdmin ? 'Enter name...' : 'Enter your name...'}
              value={searchName} onChange={e => setSearchName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              style={{ width: '100%', padding: '10px 14px', border: searchError ? '1px solid red' : '1px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
            />
            {searchError && <p style={{ color: 'red', fontSize: '12px', margin: '4px 0 0' }}>{searchError}</p>}
          </div>
          <button onClick={handleSearch} disabled={searchLoading}
            style={{ padding: '10px 24px', background: '#f1870e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', opacity: searchLoading ? 0.7 : 1 }}>
            {searchLoading ? 'Searching...' : 'Search'}
          </button>
          {searchResult && (
            <button onClick={clearSearch} style={{ padding: '10px 20px', background: '#fff3e0', color: '#f1870e', border: '1px solid #f1870e', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Clear</button>
          )}
        </div>
      </div>

      {renderSearchResult()}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button style={tabBtn(view === 'summary')}  onClick={() => setView('summary')}>Overall</button>
        <button style={tabBtn(view === 'weekly')}   onClick={() => setView('weekly')}>Weekly</button>
        <button style={tabBtn(view === 'monthly')}  onClick={() => setView('monthly')}>Monthly</button>
        <button style={tabBtn(view === 'schedule')} onClick={() => setView('schedule')}>Schedule</button>
      </div>

      {view === 'summary'  && renderSummary()}
      {view === 'weekly'   && renderWeekly()}
      {view === 'monthly'  && renderMonthly()}
      {view === 'schedule' && renderSchedule()}
    </div>
  );
};

export default RemunerationSummary;